/**
 * AgentThread domain types matching backend roboto.ai.chat module
 */
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewDefinitionV1 } from "@/shared/domain/views";
import { LoggerService } from "@/shared/services/LoggerService";

import { type ClientToolSpec } from "./clientTools/ClientTools";

/**
 * Metadata about an available model profile, returned by the API.
 */
export interface ModelProfileResponse {
  id: string;
  label: string;
  description: string;
  is_default: boolean;
}

/**
 * Enumeration of possible roles in a thread conversation.
 */
export enum AgentThreadRole {
  USER = "user",
  ASSISTANT = "assistant",
  ROBOTO = "roboto",
}

/**
 * Enumeration of possible message generation states.
 */
export enum AgentThreadMessageStatus {
  NOT_STARTED = "not_started",
  GENERATING = "generating",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

/**
 * Who is allowed to read an AgentThread after it has been created.
 *
 * Mirrors backend ``ThreadVisibility``. ``PRIVATE`` restricts reads to the
 * ``created_by`` user and Roboto admins; ``ORG`` opens the thread to every
 * member of ``org_id``. Wire values are lowercase and match the database's
 * ``CHECK (visibility IN ('private', 'org'))`` constraint from chats_019.
 *
 * Co-located with the rest of the AgentThread domain so threads-page code
 * doesn't need to reach into the Agents domain for a shape that the
 * backend models as its own enum.
 */
export enum ThreadVisibility {
  PRIVATE = "private",
  ORG = "org",
}

/**
 * Which surface owns a thread's conversation. Mirrors backend ``ThreadOrigin``.
 *
 * A thread that started outside Roboto — today, by mentioning @Roboto in a
 * Slack channel — is mirrored into that conversation, and only that surface may
 * append to it. The API refuses new turns from anywhere else with
 * ``RobotoThreadReadOnlyException``; the way to continue such a thread in Roboto
 * is to fork it, which yields a private, writable copy. Absent or ``API`` means
 * a thread of Roboto's own, which is writable as usual.
 */
export enum ThreadOrigin {
  API = "api",
  SLACK = "slack",
}

/**
 * Lifecycle state of a thread session — mirrors backend ``AgentSessionStatus``.
 */
export enum AgentThreadStatus {
  NOT_STARTED = "not_started",
  USER_TURN = "user_turn",
  ROBOTO_TURN = "roboto_turn",
  CLIENT_TOOL_TURN = "client_tool_turn",
  /**
   * The runner exhausted its corrective re-prompt budget without satisfying
   * every declared goal for the latest turn; the session needs human
   * intervention before it can continue.
   */
  GOALS_FAILED = "goals_failed",
}

/**
 * Whether the backend still owes work on this thread, so new messages can
 * still arrive and polling will eventually resolve. True only for the two
 * "in-flight" states: the server is generating (`ROBOTO_TURN`) or a client
 * tool is mid-execution (`CLIENT_TOOL_TURN`).
 *
 * Every other state is idle or terminal: `USER_TURN`, `NOT_STARTED`, and the
 * permanent `GOALS_FAILED`, which needs human intervention to leave. Callers
 * that refetch while a thread is active must gate on this rather than on
 * `!== USER_TURN`; the latter polls `GOALS_FAILED` and `NOT_STARTED`
 * indefinitely, since those never transition on their own.
 */
export function isActiveThreadStatus(
  status: AgentThreadStatus | undefined,
): boolean {
  return (
    status === AgentThreadStatus.ROBOTO_TURN ||
    status === AgentThreadStatus.CLIENT_TOOL_TURN
  );
}

/**
 * Lifecycle of a per-turn declared goal — mirrors backend ``AgentGoalStatus``.
 * Goals begin PENDING, then transition to ACHIEVED or FAILED (terminal).
 */
export enum AgentThreadGoalStatus {
  PENDING = "pending",
  ACHIEVED = "achieved",
  FAILED = "failed",
}

/**
 * Customer-visible read shape of a goal declared on an agent session — mirrors
 * backend ``AgentSessionGoalRecord``. Goals are declared per-turn (attached
 * to a USER-role message) and the runner enforces achievement before the turn
 * completes.
 */
export interface AgentThreadGoalRecord {
  /**
   * Discriminator selecting which goal type this row's data conforms to,
   * e.g. ``"dataset_summary"``.
   */
  goal_type: string;
  /** Validated goal payload; shape determined by ``goal_type``. */
  goal_data: Record<string, unknown>;
  /** Current lifecycle state of the goal. */
  status: AgentThreadGoalStatus;
  /**
   * Index in the session's full messages list of the USER-role message that
   * declared this goal. Use to render goal chips on the right turn.
   */
  message_sequence_num: number;
  /** Timestamp when the goal was registered. */
  created: string;
  /**
   * Timestamp when the goal transitioned to a terminal state, or null while
   * still PENDING.
   */
  concluded_at?: string | null;
  /**
   * ``tool_use_id`` of the achieve-tool invocation most recently associated
   * with this goal, or ``null`` if none was recorded. The wire format always
   * sends this key (as ``null`` when unset), so it is required rather than
   * optional — callers handle exactly two states, string or ``null``, never a
   * missing field. Use with ``useToolDetails`` to fetch the achieve-tool's
   * input/result and derive the goal's outcome (applied triage labels, the
   * persisted summary, created events).
   */
  achieve_tool_use_id: string | null;
}

/**
 * Lifecycle state of a top-level agent task — mirrors backend ``AgentTaskStatus``.
 * At most one task per thread is IN_PROGRESS at a time.
 */
export enum AgentTaskStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
}

/**
 * Lifecycle state of a sub-task — mirrors backend ``AgentSubtaskStatus``. Sub-tasks
 * are never activated independently, so they have no in-progress state.
 */
export enum AgentSubtaskStatus {
  PENDING = "pending",
  DONE = "done",
}

/**
 * Position in the conversation where a top-level task's active span begins or ends.
 */
export interface AgentTaskBoundary {
  /** Index of the assistant message whose tool call stamped this boundary. */
  message_sequence_num: number;
  /** Index of the tool-call content block within that message. */
  content_sequence_num: number;
}

/** A lightweight checklist item under a top-level task. */
export interface AgentSubtask {
  title: string;
  status: AgentSubtaskStatus;
}

/**
 * A top-level task the agent is tracking within a thread — mirrors backend
 * ``AgentTask``.
 */
export interface AgentTask {
  /** Thread-monotonic id, stable for the life of the thread. */
  task_id: number;
  /** Display order among the thread's top-level tasks. */
  position: number;
  title: string;
  description?: string | null;
  /** Outcome recorded when the task was completed; null until then. */
  conclusion?: string | null;
  status: AgentTaskStatus;
  /** Where the task most recently became in-progress; null if never started. */
  start?: AgentTaskBoundary | null;
  /** Where the task was completed; null until then. */
  end?: AgentTaskBoundary | null;
  subtasks: AgentSubtask[];
}

/**
 * Enumeration of different types of content within thread messages.
 */
export enum AgentThreadContentType {
  TEXT = "text",
  TOOL_USE = "tool_use",
  TOOL_RESULT = "tool_result",
  ERROR = "error",
  DELETED = "deleted",
  COMPRESSION_FILLER = "compression_filler",
  CLIENT_CONTEXT = "client_context",
}

/**
 * Text content within a thread message.
 */
export interface AgentThreadTextContent {
  text: string;
}

export const isAgentThreadTextContent = (
  obj: unknown,
): obj is AgentThreadTextContent => {
  return typeof obj === "object" && obj !== null && "text" in obj;
};

/**
 * Tool usage request content within a thread message.
 */
export interface AgentThreadToolUseContent {
  content_type: "tool_use";
  tool_name: string;
  tool_use_id: string;
  /** Parsed tool input parameters chosen by the LLM (provider-agnostic). */
  input?: Record<string, unknown>;
  raw_request?: Record<string, unknown>;
  /** True when the server writes this client tool use's result itself, so
   *  the browser must not run it. */
  answered_by_server?: boolean;
}

export const isAgentThreadToolUseContent = (
  obj: unknown,
): obj is AgentThreadToolUseContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_use"
  );
};

/**
 * Tool execution result content within a thread message.
 */
export interface AgentThreadToolResultContent {
  content_type: "tool_result";
  tool_name: string;
  tool_use_id: string;
  runtime_ms: number;
  status: string;
  /**
   * Files this invocation produced. Unlike `raw_response`, this survives thread-read sanitization;
   * read it via `resolveArtifacts`. Arrives as explicit `null` when empty, not `undefined`.
   */
  artifacts?: Record<string, unknown>[] | null;
  /**
   * What the tool returned: free-form text, or a JSON object or array of
   * structured data. Mirrors the SDK's ``AgentToolResultContent.payload`` —
   * provider-agnostic, unlike the legacy ``raw_response`` envelope.
   *
   * ``undefined``/``null`` on results written before the field existed.
   * Typed ``unknown`` rather than the SDK's string-or-object union so readers
   * must narrow before use; nothing in the UI renders it yet (the raw
   * request/response detail panel still reads the admin route).
   */
  payload?: unknown;
  /**
   * Legacy provider-formatted response envelope. Deprecated on the wire in
   * favor of ``payload``; threads read over the API arrive with this field
   * stripped, so UI readers should not expect it.
   */
  raw_response?: Record<string, unknown>;
}

export const isAgentThreadToolResultContent = (
  obj: unknown,
): obj is AgentThreadToolResultContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_result"
  );
};

export interface AgentThreadToolDetailResponse {
  tool_use: AgentThreadToolUseContent;
  tool_result: AgentThreadToolResultContent;
}

/** A short-TTL presigned S3 GET URL for one artifact a tool call produced. */
export interface AgentThreadArtifactUrlResponse {
  url: string;
  content_type: string;
  bytes: number;
}

/**
 * Error content within a thread message.
 * Used when message generation fails due to an error or is cancelled by the user.
 */
export interface AgentThreadErrorContent {
  content_type: "error";
  /** User-friendly error message describing what went wrong. */
  error_message: string;
  /** Optional error code for programmatic handling. */
  error_code?: string;
}

/**
 * The `error_code` of the notice a failed turn ends with. It matches
 * `TURN_FAILED_ERROR_CODE` in `roboto_service.ai.core.agent`.
 */
export const TURN_FAILED_ERROR_CODE = "TURN_FAILED";

export const isAgentThreadErrorContent = (
  obj: unknown,
): obj is AgentThreadErrorContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "error"
  );
};

/**
 * Tombstone for a content block compression removed.
 *
 * Carries no payload — its presence records that a block once occupied this
 * slot. Appears only inside a `deleted`-tier reconstructed variant (the admin
 * prompt view): whether the block was a pure-filler text run dropped by message
 * compression or a whole tool exchange dropped by the cross-message deletion
 * pass, both land at the deleted tier. The live thread never contains one.
 */
export interface AgentThreadDeletedContent {
  content_type: "deleted";
}

export const isAgentThreadDeletedContent = (
  obj: unknown,
): obj is AgentThreadDeletedContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "deleted"
  );
};

/**
 * Stand-in for a message the compression deletion pass emptied entirely.
 *
 * A fully-deleted message keeps a single block of this type instead of an empty
 * content list, so it stays in the prompt at its role. Sent to the model as
 * `<Deleted in compression>`. Only ever appears inside a `deleted`-tier
 * reconstructed message (the admin prompt view).
 */
export interface AgentThreadCompressionFillerContent {
  content_type: "compression_filler";
}

export const isAgentThreadCompressionFillerContent = (
  obj: unknown,
): obj is AgentThreadCompressionFillerContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "compression_filler"
  );
};

/**
 * The caller's attached viewing context, carried on their own message.
 *
 * Replaces a `<ctx>{json}</ctx>` marker persisted as a separate ROBOTO-role message
 * (ENG-2185). Threads written before that change still carry the marker — see
 * `parseContextFromMessage`, which reads both. Carries no `text`, so it never renders.
 */
export interface AgentThreadClientContextContent {
  content_type: "client_context";
  context: ClientViewingContext;
}

export const isAgentThreadClientContextContent = (
  obj: unknown,
): obj is AgentThreadClientContextContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "client_context"
  );
};

/**
 * Type alias for all possible content types within thread messages.
 */
export type AgentThreadContent =
  | AgentThreadTextContent
  | AgentThreadToolUseContent
  | AgentThreadToolResultContent
  | AgentThreadErrorContent
  | AgentThreadDeletedContent
  | AgentThreadCompressionFillerContent
  | AgentThreadClientContextContent;

/** Content types already warned about, so a 500 ms poll re-render doesn't spam. */
const warnedUnknownContentTypes = new Set<string>();

/**
 * Compile-time exhaustiveness backstop for content-dispatch sites.
 *
 * Call this from the final `else` of a guard chain over
 * {@link AgentThreadContent}. The parameter is typed `never`, so adding a
 * member to the union makes every dispatch site that neither renders nor
 * deliberately ignores the new type fail to typecheck — a new content type
 * cannot ship invisible by silently falling through.
 *
 * Forward compatibility is preserved at runtime: a block from a newer server
 * fails every guard, lands here, renders as nothing (`null`), and logs one
 * dev-only warning per unknown `content_type`.
 */
export function reportUnknownAgentThreadContent(content: never): null {
  const contentType = String(
    (content as { content_type?: unknown }).content_type,
  );
  if (!warnedUnknownContentTypes.has(contentType)) {
    warnedUnknownContentTypes.add(contentType);
    LoggerService.warn(
      `Unknown agent thread content type "${contentType}" — rendering nothing.`,
      content,
    );
  }
  return null;
}

/**
 * What the Roboto client (e.g. the Web UI) is currently viewing when the
 * user composed a message. Passed to the agent as implicit context for
 * resolving deictic references like "this dataset" or "those files" without
 * requiring the user to spell them out. Informational only — does not gate
 * authorization or scope tools.
 */
export interface ClientViewingContext {
  /**
   * IDs of datasets the user is currently viewing or has selected.
   */
  dataset_ids: string[];

  /**
   * IDs of files the user is currently viewing or has selected.
   */
  file_ids: string[];

  /**
   * IDs of collections the user is currently viewing or has selected.
   */
  collection_ids: string[];

  /**
   * IDs of devices the user is currently viewing or has selected. Device IDs
   * are user-chosen rather than minted by Roboto, so unlike the other fields
   * here a value may look like anything at all.
   */
  device_ids: string[];

  /**
   * IDs of device types the user is currently viewing.
   */
  device_type_ids: string[];

  /**
   * IDs of sessions the user is currently viewing.
   */
  session_ids: string[];

  /**
   * IDs of Views (saved searches) applied to a resource table the user is
   * looking at. Informational: it lets the agent resolve "this View" without
   * the user naming it.
   */
  view_ids: string[];

  /**
   * The resource table on screen, when the user composed the message from
   * one: which table, and its live state in the shape a View stores. Sent
   * whether or not a View is loaded, so ad-hoc filters are visible to the
   * agent and "save what I'm looking at as a View" names something concrete.
   */
  table_state?: {
    target: QueryTarget;
    definition: ViewDefinitionV1;
  };

  /**
   * Epoch nanoseconds the client is rendering as t=0, as a decimal string
   * because `JSON.stringify` throws on a bigint. Present only while the user
   * is reading timestamps relative to an anchor; it lets the agent resolve a
   * bare relative time they type ("around 65 s"). The agent's replies stay in
   * absolute nanoseconds either way.
   */
  display_time_anchor_ns?: string;

  /**
   * What the client shows the user as the name of that t=0 — "Start of
   * workspace", or a picked timestamp rendered as local time. It lets the agent
   * name the instant an offset is counted from ("10 s after your t=0, the start
   * of the workspace") rather than leaving the reader to guess which one it
   * meant. Absent whenever {@link display_time_anchor_ns} is.
   */
  display_time_anchor_label?: string;

  /**
   * State of the visualizer, when the user composed the message from the
   * visualizer view. A relatively opaque JSON blob.
   */
  visualizer_state?: Record<string, unknown>;

  /**
   * Miscellaneous client-supplied context that doesn't fit the typed fields
   * above.
   */
  misc_context?: Record<string, unknown>;
}

/**
 * A single message within a thread conversation.
 */
export interface AgentThreadMessage {
  /** The role of the message sender (user, assistant, or roboto). */
  role: AgentThreadRole;

  /** List of content blocks that make up this message. */
  content: AgentThreadContent[];

  /** Current generation status of this message. */
  status?: AgentThreadMessageStatus;

  /** ISO timestamp when this message was created. */
  created?: string;

  /**
   * User ID of the person who sent this message. Set only on user messages a
   * person sent; null or absent on assistant and roboto messages, on messages
   * an agent launch, trigger or Slack mention wrote, and on messages sent
   * before the server recorded authors.
   */
  created_by?: string | null;
}

/**
 * Complete record of an agent thread.
 */
export interface AgentThreadRecord {
  /** Unique identifier for this agent thread. */
  thread_id: string;

  /** Timestamp when the thread was created (ISO string). */
  created: string;

  /** User ID of the person who created this thread. */
  created_by: string;

  /** Organization ID that owns this thread. */
  org_id: string;

  /** Complete list of messages in the conversation. */
  messages: AgentThreadMessage[];

  /** Token used for incremental updates and synchronization. */
  continuation_token: string;

  /** Current status of the thread. */
  status: AgentThreadStatus;

  /**
   * A sentence for display saying what the agent is doing that the thread's
   * messages don't show yet. For example, while the service summarizes the
   * thread's older messages so the conversation fits the model's context
   * window, it is "Summarizing older messages to fit the model's context
   * window". While a server tool runs, it is the tool's latest progress
   * report, such as "Starting the Python sandbox". Null or absent when there
   * is no such activity.
   */
  activity?: string | null;

  /**
   * Who can read this thread. ``PRIVATE`` restricts reads to the
   * ``created_by`` user and Roboto admins; ``ORG`` opens it to every
   * member of ``org_id``. Backend always serializes this field with a
   * default of ``PRIVATE`` (see ``ThreadVisibility`` in the SDK), so the
   * field is required on the wire.
   */
  visibility: ThreadVisibility;

  /**
   * Which surface owns the conversation. Absent for Roboto's own threads; set
   * by the backend and never by a client. See ``ThreadOrigin``.
   */
  origin?: ThreadOrigin | null;

  /** Optional title for the thread. */
  title?: string;

  /** Model profile used for this thread. */
  model_profile?: string;

  /** If this thread was forked from another, the source thread's id. */
  forked_from_thread_id?: string | null;

  /** Message sequence number in the source thread that this fork was taken from. */
  forked_from_message_sequence_num?: number | null;

  /**
   * Agent that launched this thread, when the thread was started via
   * ``POST /v1/ai/agents/<agent_id>/launch``. Threads created directly
   * with ``POST /v1/ai/threads`` (no Agent template) have this field
   * null/undefined. Audit-only — the column is intentionally not a FK
   * server-side so the lineage survives the referenced Agent being
   * renamed or deleted.
   */
  created_from_agent_id?: string | null;

  /**
   * Trigger whose ``start_agent`` target launched this thread. Null/undefined
   * on every other thread, including one launched from the same agent by a
   * person: ``created_from_agent_id`` names the agent that ran, this names
   * what decided to run it. Audit-only and not a FK, for the same reason as
   * the agent lineage.
   */
  created_from_trigger_id?: string | null;

  /**
   * ISO timestamp of when the requesting user pinned this thread, or
   * null/undefined if they have not.
   *
   * A pin is a personal bookmark, so unlike every other field here this one
   * is viewer-relative: two users reading the same thread see different
   * values. Only `GET /v1/ai/threads` and `POST /v1/ai/threads/search`
   * populate it; reads of a single thread and the admin thread listings
   * leave it absent whatever the pin state.
   */
  pinned_at?: string | null;

  /**
   * Goals declared across this thread's turns, ordered by allocation. ``null``
   * or ``undefined`` means goals were not loaded for this record (lightweight
   * reads may omit; full-thread reads populate); an empty array means the
   * thread never declared any goals.
   */
  goals?: AgentThreadGoalRecord[] | null;

  /**
   * The thread's task list, ordered by ``position``. ``null`` or ``undefined``
   * means tasks were not loaded for this record; an empty array means the thread
   * has none.
   */
  tasks?: AgentTask[] | null;

  /**
   * A short note about the turn in progress, e.g. that the worker running it
   * stopped unexpectedly and the turn is being retried. `null` (or absent)
   * when there is nothing to report.
   */
  turn_notice?: string | null;
}

/**
 * Incremental update to an agent thread.
 */
export interface AgentThreadRecordDelta {
  /** New or updated messages indexed by their position in the conversation. */
  messages_by_idx: Record<number, AgentThreadMessage>;

  /** Updated token for the next incremental synchronization. */
  continuation_token: string;

  /**
   * Discard held messages from this index onward before applying this delta.
   *
   * A delta's messages normally extend what we already hold, because the
   * server sends only content we have not seen. That breaks when a turn is
   * abandoned partway and regenerated: the replacement reuses the same index,
   * so what we hold there is text from an attempt that no longer exists, and
   * appending would splice the real reply onto a discarded draft.
   *
   * When set, truncate to indices strictly below it, then apply
   * `messages_by_idx` and adopt `continuation_token` as usual — the server has
   * rewound the stream and will resend from that point.
   *
   * Explicitly `| null`, because that is what the wire carries in the common
   * case: the server's model declares this optional and pydantic serializes an
   * unset optional as JSON `null`, not as an absent key. Consumers must treat
   * `null` and `undefined` alike as "no rewind".
   */
  reset_from_message_sequence_num?: number | null;

  /** Updated status of the thread. */
  status?: AgentThreadStatus;

  /**
   * The thread's current activity (see `AgentThreadRecord.activity`). Unlike
   * most optional fields, null or absent does not mean "unchanged": every
   * delta carries the current activity, so null or absent means there is none,
   * and the client replaces the activity it holds with this value.
   */
  activity?: string | null;

  /**
   * The note about the turn in progress (see `AgentThreadRecord.turn_notice`).
   * As with `activity`, every delta carries the current note, so null or
   * absent means there is none now, not "unchanged".
   */
  turn_notice?: string | null;

  /** Updated title of the thread. */
  title?: string;

  /**
   * Latest snapshot of all goals declared in the thread. ``null`` or
   * ``undefined`` means "no change since the last delta the BE sent" — retain
   * the previously held snapshot. An empty array means the thread has no
   * declared goals; a populated array is the authoritative latest snapshot.
   */
  goals?: AgentThreadGoalRecord[] | null;

  /**
   * Latest snapshot of the thread's task list, ordered by position. ``null`` or
   * ``undefined`` means "no change since the last delta" — retain the previously
   * held snapshot. An empty array means the thread has no tasks; a populated
   * array is the authoritative latest snapshot.
   */
  tasks?: AgentTask[] | null;
}

// ClientToolSpec is canonically defined in clientTools/ClientTools.ts.
// Re-exported so consumers that import from AgentThreadRecord continue to
// work without changes.
export { type ClientToolSpec };

/**
 * Spec for invoking a published skill as part of a turn trigger. Embedded in
 * StartAgentThreadRequest and SendMessageRequest. Server resolves access (org / archived /
 * private) and version selection.
 */
export interface InvokeSkillSpec {
  skill_id: string;
  /** Optional version number. Must exist on the skill (any version is invokable).
   * Omit to use the latest (MAX(version)) row. Note this is the structural
   * latest, not the caller's pinned ai_version — manual chip invocation
   * always runs MAX regardless of pin. */
  version?: number | null;
}

/**
 * The slice of data an agent is expected to analyze. Mirrors the SDK's
 * ``roboto.ai.core.AnalysisScope``: delivered to every server-side tool
 * invocation, with individual tools opting in to honoring it. Fields set
 * to null/undefined are unconstrained on that dimension.
 *
 * Bounds are `bigint` because ns-epoch values exceed
 * `Number.MAX_SAFE_INTEGER`; senders must serialize with a bigint-aware
 * replacer (see `EventService.createEvent` for the `rawJSON` pattern).
 */
export interface AnalysisScope {
  /** Lower bound (inclusive) of the analysis window, in nanoseconds since
   * the Unix epoch. */
  start_time?: bigint | null;
  /** Upper bound (inclusive) of the analysis window, in nanoseconds since
   * the Unix epoch. */
  end_time?: bigint | null;
}

/**
 * Request payload for starting a new thread session.
 */
export interface StartAgentThreadRequest {
  /** Optional client-viewing context describing what the user is looking at. */
  client_context?: ClientViewingContext;

  /** Initial messages to start the conversation with. May be empty when at
   * least one of `goals` or `invoke_skills` is supplied; in that case the
   * server synthesizes a minimal user message so the LLM has a turn-initiating
   * prompt. */
  messages: AgentThreadMessage[];

  /** Optional system prompt to customize AI assistant behavior. */
  system_prompt?: string;

  /** Optional model profile ID for the thread session. */
  model_profile?: string;

  /** Optional client-side tools available for the LLM to invoke. */
  client_tools?: ClientToolSpec[];

  /** Skills to invoke at session start, in order. For each entry the server fabricates
   * a `load_skill` tool_use + tool_result pair in the transcript after any seeded
   * messages. Pass a single-element list for the common "invoke one skill" case. */
  invoke_skills?: InvokeSkillSpec[];
}

/**
 * Request payload for sending a new message in a thread session.
 */
export interface SendMessageRequest {
  /** Optional client-viewing context describing what the user is looking at. */
  client_context?: ClientViewingContext;
  /** Message to send. Optional when at least one of `goals` or `invoke_skills`
   * is supplied; in that case the server synthesizes a minimal user message
   * if needed (goals attach to it; chips alone trigger the turn). */
  message?: AgentThreadMessage;
  /** Optional client-side tools available for the LLM to invoke. */
  client_tools?: ClientToolSpec[];
  /** Skills to invoke as part of this turn, in order. Author-only and only allowed
   * during USER_TURN. For each entry the server fabricates a `load_skill` tool_use +
   * tool_result pair in the transcript after `message` (if any). Pass a
   * single-element list for the common "invoke one skill" case. */
  invoke_skills?: InvokeSkillSpec[];
}

/**
 * Result of a client-side tool execution, submitted by the browser.
 *
 * Uses `output` for the tool's structured domain response (provider-agnostic),
 * as opposed to `AgentThreadToolResultContent.raw_response` which may hold
 * provider-specific payloads from the persistence layer.
 */
export interface ClientToolResult {
  /** Identifier of the tool invocation this result corresponds to. */
  tool_use_id: string;
  /** Name of the tool that was executed. */
  tool_name: string;
  /** Wall-clock execution time of the tool in milliseconds. */
  runtime_ms: number;
  /** Outcome of the tool execution. */
  status: "success" | "error";
  /** Structured output returned by the tool. */
  output?: Record<string, unknown>;
}

/**
 * Request payload for submitting client-side tool execution results.
 */
export interface SubmitToolResultsRequest {
  /** Results from executing client-side tools. */
  tool_results: ClientToolResult[];
  /** Optional updated list of client-side tools available for subsequent turns. */
  client_tools?: ClientToolSpec[];
}

/**
 * Overall direction of a piece of AI-thread feedback.
 */
export enum FeedbackSentiment {
  POSITIVE = "positive",
  NEGATIVE = "negative",
}

/**
 * Taxonomy of feedback categories. Which categories are valid depends on the
 * selected sentiment; ``OTHER`` is always valid (with required notes).
 */
export enum FeedbackCategory {
  // Negative
  INCORRECT = "incorrect",
  INCOMPLETE = "incomplete",
  TOOL_FAILURE = "tool_failure",
  REFUSED_VALID_REQUEST = "refused_valid_request",
  FORMATTING = "formatting",
  SLOW = "slow",
  UNSAFE = "unsafe",
  // Positive
  CORRECT = "correct",
  HELPFUL = "helpful",
  GOOD_TOOL_USE = "good_tool_use",
  // Either
  OTHER = "other",
}

export const NEGATIVE_CATEGORIES: ReadonlyArray<FeedbackCategory> = [
  FeedbackCategory.INCORRECT,
  FeedbackCategory.INCOMPLETE,
  FeedbackCategory.TOOL_FAILURE,
  FeedbackCategory.REFUSED_VALID_REQUEST,
  FeedbackCategory.FORMATTING,
  FeedbackCategory.SLOW,
  FeedbackCategory.UNSAFE,
];

export const POSITIVE_CATEGORIES: ReadonlyArray<FeedbackCategory> = [
  FeedbackCategory.CORRECT,
  FeedbackCategory.HELPFUL,
  FeedbackCategory.GOOD_TOOL_USE,
];

/**
 * Human-readable label for a feedback category. Single source of truth — the
 * wizard, the admin filter dropdown, and the admin table all render via this
 * map so labels can't drift apart.
 */
export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  // Negative
  [FeedbackCategory.INCORRECT]: "Incorrect / hallucinated",
  [FeedbackCategory.INCOMPLETE]: "Incomplete",
  [FeedbackCategory.TOOL_FAILURE]: "Tool failure",
  [FeedbackCategory.REFUSED_VALID_REQUEST]: "Refused valid request",
  [FeedbackCategory.FORMATTING]: "Formatting",
  [FeedbackCategory.SLOW]: "Slow",
  [FeedbackCategory.UNSAFE]: "Unsafe",
  // Positive
  [FeedbackCategory.CORRECT]: "Correct",
  [FeedbackCategory.HELPFUL]: "Helpful",
  [FeedbackCategory.GOOD_TOOL_USE]: "Good tool use",
  // Either
  [FeedbackCategory.OTHER]: "Other",
};

/**
 * Request payload for submitting feedback on an assistant message.
 *
 * One feedback row exists per (thread, message, user). Resubmitting from the
 * same user replaces sentiment/categories/notes in place.
 */
export interface SubmitFeedbackRequest {
  sentiment: FeedbackSentiment;
  /** Zero or more categories. ``OTHER`` requires ``notes``. */
  categories: FeedbackCategory[];
  notes?: string | null;
}

/**
 * Admin-only triage update payload.
 *
 * Fields left undefined are not modified. ``admin_label`` and ``admin_note``
 * may be set to ``null`` to clear the stored value. ``resolved`` is a non-null
 * boolean column; toggle it with ``true``/``false`` (which stamps/clears
 * ``resolved_at``/``resolved_by`` server-side) — ``null`` is rejected.
 */
export interface AdminUpdateFeedbackRequest {
  admin_label?: string | null;
  admin_note?: string | null;
  resolved?: boolean;
}

/**
 * Customer-facing projection of a feedback record.
 *
 * Returned by non-admin endpoints (`POST /v1/ai/threads/<id>/messages/<idx>/feedback`
 * and `GET /v1/ai/threads/<id>/feedback`). The wire shape carries no
 * `admin_label`, `admin_note`, `resolved`, `resolved_by`, or `resolved_at`
 * for non-admin callers — the privacy guarantee lives at the route boundary
 * (server-side `UserFeedbackRecord.from_admin_record`), not in the TypeScript
 * type system: because `AgentFeedbackRecord` extends this interface, an
 * admin record IS structurally assignable to a `UserFeedbackRecord`. The
 * type system can't stop a route from leaking; the route itself must.
 */
export interface UserFeedbackRecord {
  feedback_id: string;
  thread_id: string;
  message_sequence_num: number;
  org_id: string;
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
  sentiment: FeedbackSentiment;
  categories: FeedbackCategory[];
  notes?: string | null;
}

/**
 * Admin view of a feedback record. Used only by admin endpoints. Extends
 * `UserFeedbackRecord` with the triage columns; structurally assignable to
 * the user record so the type system does not enforce the projection — see
 * the comment above `UserFeedbackRecord` for where the boundary actually is.
 */
export interface AgentFeedbackRecord extends UserFeedbackRecord {
  admin_label?: string | null;
  admin_note?: string | null;
  resolved: boolean;
  resolved_by?: string | null;
  resolved_at?: string | null;
}
